import { useEffect, useState, useCallback } from "react";
import UpdatePanel from "@/components/UpdatePanel";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Play, RefreshCcw, Loader2, Download, ListRestart, Search } from "lucide-react";
import ToolLogo from "@/components/ToolLogo";
import StopScraperButton from "@/components/StopScraperButton";
import ScrapeModeSelector from "@/components/ScrapeModeSelector";

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  const units = ["B", "kB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

export default function AlientechScrapeControl() {
  const [status, setStatus] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [maxPages, setMaxPages] = useState("");
  const [starting, setStarting] = useState(false);
  const [startingMissing, setStartingMissing] = useState(false);
  const [syncingIndex, setSyncingIndex] = useState(false);
  const [useFlaresolverr, setUseFlaresolverr] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: st }, { data: cov }] = await Promise.all([
        api.get("/alientech/scrape/status"),
        api.get("/alientech/scrape/coverage"),
      ]);
      setStatus(st);
      setCoverage(cov);
    } catch (err) {
      console.warn("status error", err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const start = async () => {
    setStarting(true);
    try {
      const params = {};
      if (maxPages) params.max_pages = Number(maxPages);
      params.use_flaresolverr = useFlaresolverr;
      await api.post("/alientech/scrape/run", null, { params });
      toast.success("Alientech-Scraper gestartet");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Konnte Scraper nicht starten";
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  const startMissing = async () => {
    setStartingMissing(true);
    try {
      await api.post("/alientech/scrape/run", null, {
        params: { only_missing: true, use_flaresolverr: useFlaresolverr },
      });
      toast.success("Hole fehlende Seiten nach…");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Konnte Nachhol-Lauf nicht starten";
      toast.error(msg);
    } finally {
      setStartingMissing(false);
    }
  };

  const syncSearchIndex = async () => {
    setSyncingIndex(true);
    try {
      const { data } = await api.post("/alientech/search-index/sync");
      toast.success(`Such-Index aktualisiert (${data.synced.toLocaleString("de-DE")} Einträge)`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Konnte Such-Index nicht aktualisieren";
      toast.error(msg);
    } finally {
      setSyncingIndex(false);
    }
  };

  return (
    <div data-testid="alientech-scrape-page" className="space-y-8">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ToolLogo source="alientech" />
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D32222]">
            Alientech · Scrape Control
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Control card */}
        <div className="rounded-xl border border-neutral-800 bg-panel p-6 space-y-5">
          <div className="text-sm uppercase tracking-widest text-neutral-500">
            Neuen Lauf starten
          </div>
          <div>
            <Label className="text-xs text-neutral-400">Max. Seiten (leer = alle, à 100 Einträge)</Label>
            <Input
              data-testid="alientech-max-pages-input"
              type="number"
              min={1}
              value={maxPages}
              onChange={(e) => setMaxPages(e.target.value)}
              placeholder="z. B. 10"
              className="mt-1 bg-neutral-900 border-neutral-800 text-neutral-100 focus-visible:ring-[#D32222]"
            />
          </div>
          <ScrapeModeSelector direct={useFlaresolverr} onChange={setUseFlaresolverr} disabled={status?.running} testId="alientech-scrape-mode-selector" />
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              data-testid="alientech-start-scrape-btn"
              onClick={start}
              disabled={starting || status?.running}
              className="bg-[#D32222] hover:bg-[#E13636] text-white font-medium"
            >
              {starting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Scraper starten
            </Button>
            <StopScraperButton endpoint="/alientech/scrape/stop" running={status?.running} onStopped={load} testId="alientech-stop-scrape-btn" />
            <Button
              data-testid="alientech-start-missing-btn"
              onClick={startMissing}
              disabled={startingMissing || status?.running || !coverage?.missing_count}
              variant="outline"
              className="border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-100"
              title={
                coverage?.missing_count
                  ? `${coverage.missing_count} fehlende Seiten nachholen`
                  : "Keine fehlenden Seiten bekannt"
              }
            >
              {startingMissing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ListRestart className="w-4 h-4 mr-2" />
              )}
              Fehlende nachholen
              {coverage?.missing_count ? ` (${coverage.missing_count})` : ""}
            </Button>
            <Button
              data-testid="alientech-refresh-status-btn"
              variant="outline"
              className="border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-100"
              onClick={load}
            >
              <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              data-testid="alientech-sync-index-btn"
              onClick={syncSearchIndex}
              disabled={syncingIndex}
              variant="outline"
              title="Überträgt den aktuellen Datenstand in den Meilisearch-Index für die Sofortsuche"
              className="border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-100"
            >
              {syncingIndex ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Such-Index aktualisieren
            </Button>
            <a
              href={`${api.defaults.baseURL}/alientech/scrape/output`}
              data-testid="alientech-download-json-link"
              className="ml-auto inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-[#E13636] transition"
            >
              <Download className="w-4 h-4" /> alientech_output.json
            </a>
          </div>
        </div>

        {/* Status card */}
        <div className="rounded-xl border border-neutral-800 bg-panel p-6">
          <div className="text-sm uppercase tracking-widest text-neutral-500 mb-4">
            Status
          </div>
          <div className="grid grid-cols-2 gap-4">
            <StatusPill
              label="Zustand"
              value={
                status?.running ? (
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    aktiv (pid {status.pid})
                  </span>
                ) : (
                  <span className="text-neutral-400">idle</span>
                )
              }
              testid="alientech-status-running"
            />
            <StatusPill
              label="Einträge in DB"
              value={
                <span
                  className="tabular-nums text-[#E13636]"
                  data-testid="alientech-status-db-count"
                >
                  {status?.items_in_db?.toLocaleString("de-DE") ?? "—"}
                </span>
              }
            />
            <StatusPill
              label="alientech_output.json"
              value={formatBytes(status?.output_json_size)}
              testid="alientech-status-output-size"
            />
            <StatusPill
              label="Letzter Fortschritt"
              value={
                <span className="text-[11px] font-mono text-neutral-400 line-clamp-1">
                  {status?.last_progress || "—"}
                </span>
              }
              testid="alientech-status-last-progress"
            />
          </div>
        </div>
      </div>

      {/* Coverage */}
      <div className="rounded-xl border border-neutral-800 bg-panel p-6 space-y-4">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222] mb-2">
              Seiten-Abdeckung
            </div>
            <p className="text-sm text-neutral-400 max-w-2xl">
              Die Quelle liefert die Daten seitenweise (à 100 Einträge). Hier
              siehst du, welche Seiten bereits erfolgreich geholt wurden und
              welche noch fehlen (z. B. wegen Rate-Limiting) — FlareSolverr
              springt für einzelne blockierte Anfragen automatisch ein, bevor
              eine Seite als fehlend markiert wird.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <MiniStat
              label="Seiten gesamt"
              value={coverage?.total_pages ?? "—"}
              testid="alientech-coverage-total"
            />
            <MiniStat
              label="Vollständig"
              value={coverage?.ok_pages ?? "—"}
              accent="text-emerald-400"
              testid="alientech-coverage-ok"
            />
            <MiniStat
              label="Fehlend"
              value={coverage?.missing_count ?? "—"}
              accent={coverage?.missing_count ? "text-rose-400" : "text-neutral-500"}
              testid="alientech-coverage-missing"
            />
          </div>
        </div>
        {coverage?.missing_count > 0 && (
          <div data-testid="alientech-missing-pages" className="flex flex-wrap gap-1.5">
            {coverage.missing_pages.slice(0, 60).map((p) => (
              <span
                key={p}
                className="text-[10px] font-mono border border-rose-500/40 bg-rose-500/10 text-rose-300 rounded px-1.5 py-0.5"
              >
                {p}
              </span>
            ))}
            {coverage.missing_pages.length > 60 && (
              <span className="text-[10px] text-neutral-500 px-1.5 py-0.5">
                +{coverage.missing_pages.length - 60} weitere
              </span>
            )}
          </div>
        )}
      </div>

      <UpdatePanel provider="alientech" />

      {/* Log */}
      <div className="rounded-xl border border-neutral-800 bg-overlay overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-800">
          <div className="text-xs uppercase tracking-widest text-neutral-500">
            spider log · tail
          </div>
          <div className="text-[10px] text-neutral-600 font-mono">
            /tmp/alientech_scraper_full.log
          </div>
        </div>
        <pre
          data-testid="alientech-scrape-log"
          className="p-4 text-[11px] leading-relaxed text-neutral-300 font-mono max-h-[400px] overflow-auto whitespace-pre-wrap"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {(status?.log_tail || []).join("\n") || "keine Logs bisher…"}
        </pre>
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent = "text-neutral-100", testid }) {
  return (
    <div data-testid={testid} className="min-w-[80px]">
      <div
        className={`text-lg tabular-nums ${accent}`}
        style={{ fontFamily: "'Orbitron', sans-serif" }}
      >
        {typeof value === "number" ? value.toLocaleString("de-DE") : value}
      </div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">
        {label}
      </div>
    </div>
  );
}

function StatusPill({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-neutral-800 bg-overlay/40 p-3"
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-sm text-neutral-100">{value}</div>
    </div>
  );
}
